import useBasket from '@hooks/basket/useBasket';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { ModalID } from '@repo/ui/src/modules/UI';
import { useUI } from '@repo/ui/src/modules/UI/hooks/useUI';
import { checkoutStarted } from '@repo/ui/src/services/analytics';
import { basketLineInputToAnalytics } from '@repo/ui/src/services/analytics/factories';
import { track } from '@repo/ui/src/services/analytics/utils';
import { Basket, GeoLang } from '@repo/ui/src/types';
import { publicRuntimeConfig } from '@utils/publicRuntimeConfig';
import { useCallback, useState } from 'react';

interface Arg {
  skipSession?: boolean;
}

const trackCheckoutStarted = (basket: Basket, geo: GeoLang) =>
  track(checkoutStarted, {
    affiliation: 'website',
    currency: geo.currency,
    locale: geo.locale,
    brand: 'Traeger',
    basket_token: basket?.basketJWT,
    products: [...(basket?.lineItems?.map(basketLineInputToAnalytics) ?? [])],
    checkout_id: basket?.id ?? '',
  });

const validateCheckoutUrl = (url: string | null | undefined): string => {
  const baseUrl = publicRuntimeConfig.shopifyCheckoutUrl;
  if (!baseUrl || !url) {
    throw new Error('Invalid checkoutURL');
  }
  const checkoutURL = new URL(url);
  const baseURL = new URL(baseUrl);
  if (baseURL.origin !== checkoutURL.origin) {
    throw new Error('Invalid checkoutURL');
  }
  return url;
};

const useGoToCheckout = () => {
  const { pushModal } = useUI();
  const { geo } = useCurrentUrl();
  const { data: basket } = useBasket();
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const goToCheckout = useCallback(
    async ({ skipSession }: Arg = {}) => {
      if (!skipSession) {
        setIsMutating(true);
        setError(null);
        if (basket?.checkoutUrl) {
          try {
            trackCheckoutStarted(basket!, geo!);
            window.location.href = validateCheckoutUrl(basket.checkoutUrl);
          } catch (e) {
            setError(e as Error);
            pushModal(ModalID.error);
          } finally {
            setIsMutating(false);
          }
        } else {
          setIsMutating(false);
          pushModal(ModalID.error);
        }
        return true;
      }
      return;
    },
    [basket, geo, pushModal],
  );

  return { trigger: goToCheckout, isMutating, error };
};

export default useGoToCheckout;
